import React, { useEffect, useState } from 'react'
import { GETNET, getSpeedLabel, getDurationLabel } from '../data/plans'
import './Step3Confirm.css'

const WhatsAppIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
  </svg>
)

const HomeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
)

const CheckCircleIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01" className="confirm__check-path"/>
  </svg>
)

function buildWhatsAppMessage(order) {
  const lines = [
    '🙏 Hello GetNet Team!',
    '',
    'I have made a UPI payment for my broadband plan.',
    '',
    `*Name:* ${order.name}`,
    `*Mobile:* +91 ${order.phone}`,
    `*Plan Speed:* ${getSpeedLabel(order.speed)}`,
    `*Duration:* ${getDurationLabel(order.duration)}`,
    `*Amount Paid:* ₹${order.price.toLocaleString('en-IN')}`,
    `*UPI ID:* ${GETNET.upiId}`,
    '',
    'Please confirm my connection. Thank you!',
  ]
  return encodeURIComponent(lines.join('\n'))
}

const Step3Confirm = ({ order }) => {
  const [opened, setOpened] = useState(false)

  const waUrl = `https://wa.me/${GETNET.phone}?text=${buildWhatsAppMessage(order)}`

  // Auto-open WhatsApp after a short delay for better UX
  useEffect(() => {
    const timer = setTimeout(() => {
      window.open(waUrl, '_blank', 'noopener,noreferrer')
      setOpened(true)
    }, 1200)
    return () => clearTimeout(timer)
  }, [waUrl])


  return (
    <section className="step3" aria-labelledby="step3-heading">
      <div className="step3__card">

        {/* Success icon */}
        <div className="step3__icon-wrap" aria-hidden="true">
          <CheckCircleIcon />
        </div>

        <h2 id="step3-heading" className="step3__title">
          {opened ? 'WhatsApp Opened!' : 'Almost Done!'}
        </h2>
        <p className="step3__subtitle">
          {opened
            ? 'We received your payment request. Our team will activate your plan shortly.'
            : 'Click the button below to confirm your payment via WhatsApp.'
          }
        </p>

        {/* Order recap */}
        <div className="step3__recap" aria-label="Payment summary">
          <div className="step3__recap-row">
            <span className="step3__recap-key">Plan</span>
            <span className="step3__recap-val">
              {getSpeedLabel(order.speed)} &mdash; {getDurationLabel(order.duration)}
            </span>
          </div>
          <div className="step3__recap-row step3__recap-row--total">
            <span className="step3__recap-key">Amount</span>
            <span className="step3__recap-val step3__recap-val--gold">
              &#8377;{order.price.toLocaleString('en-IN')}
            </span>
          </div>
        </div>

        {/* WhatsApp CTA */}
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="step3__wa-btn"
          onClick={() => setOpened(true)}
          aria-label="Open WhatsApp to confirm payment with GetNet"
        >
          <WhatsAppIcon />
          {opened ? 'Open WhatsApp Again' : 'Confirm Payment on WhatsApp'}
        </a>

        {opened && (
          <p className="step3__opened-hint" role="status" aria-live="polite">
            WhatsApp opened with your payment details pre-filled. Just hit Send!
          </p>
        )}

        {/* Back to site */}
        <a
          href="https://getnet.live"
          className="step3__home-link"
          aria-label="Return to GetNet main website"
        >
          <HomeIcon />
          Back to GetNet.live
        </a>

      </div>
    </section>
  )
}

export default Step3Confirm
